import { useState } from 'react';
import { JitsiMeeting } from '@jitsi/react-sdk';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Video, Users, PhoneOff } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function GroupConferenceButton({ groupId, groupName }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [showConference, setShowConference] = useState(false);
  const [conferenceTitle, setConferenceTitle] = useState('');
  const [roomName, setRoomName] = useState(null);
  const [conferenceId, setConferenceId] = useState(null);
  const [loading, setLoading] = useState(false);

  const startConference = async () => {
    if (!conferenceTitle.trim()) {
      toast.error(t('calls.enterTitle'));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/calls/group-conference/${groupId}?title=${encodeURIComponent(conferenceTitle)}`,
        {},
        { withCredentials: true }
      );
      
      setRoomName(response.data.room_name);
      setConferenceId(response.data.conference_id);
      setShowStartDialog(false);
      setShowConference(true);
      
      toast.success(`🎓 ${t('calls.conferenceStarted')}`);
    } catch (error) {
      console.error('Error starting conference:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const endConference = async () => {
    try {
      await axios.post(`${API}/calls/end-conference/${conferenceId}`, {}, { withCredentials: true });
      setShowConference(false);
      setRoomName(null);
      setConferenceTitle('');
      toast.info(t('calls.conferenceEnded'));
    } catch (error) {
      console.error('Error ending conference:', error);
      setShowConference(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowStartDialog(true)}
        className="bg-purple-500 hover:bg-purple-600 text-white gap-2"
      >
        <Video className="h-4 w-4" />
        {t('calls.startConference')}
      </Button>

      {/* Start Conference Dialog */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('calls.startConference')}</DialogTitle>
            <DialogDescription>
              {t('calls.conferenceDescription', { group: groupName })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('calls.conferenceTitle')}</label>
              <Input
                placeholder={t('calls.titlePlaceholder')}
                value={conferenceTitle}
                onChange={(e) => setConferenceTitle(e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-800">
                🎓 {t('calls.conferenceInfo')}
              </p>
              <ul className="text-xs text-purple-700 mt-2 space-y-1">
                <li>✅ {t('calls.feature1')}</li>
                <li>✅ {t('calls.feature2')}</li>
                <li>✅ {t('calls.feature3')}</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStartDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={startConference}
              disabled={loading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {loading ? t('common.processing') : t('calls.start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conference Room Dialog */}
      <Dialog open={showConference} onOpenChange={setShowConference}>
        <DialogContent className="max-w-7xl h-[90vh] p-0">
          <div className="h-full flex flex-col">
            <div className="bg-purple-500 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {conferenceTitle}
                </h3>
                <p className="text-sm text-purple-100">{groupName} • {t('calls.conferenceMode')}</p>
              </div>
              <Button
                onClick={endConference}
                size="sm"
                variant="destructive"
                className="bg-red-500 hover:bg-red-600 gap-2"
              >
                <PhoneOff className="h-4 w-4" />
                {t('calls.endConference')}
              </Button>
            </div>
            
            {roomName && (
              <div className="flex-1">
                <JitsiMeeting
                  roomName={roomName}
                  configOverwrite={{
                    startWithAudioMuted: false,
                    startWithVideoMuted: false,
                    disableModeratorIndicator: false,
                    enableWelcomePage: false,
                    prejoinPageEnabled: false,
                    enableClosePage: false,
                  }}
                  interfaceConfigOverwrite={{
                    DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
                    SHOW_JITSI_WATERMARK: false,
                    SHOW_WATERMARK_FOR_GUESTS: false,
                    TOOLBAR_BUTTONS: [
                      'microphone',
                      'camera',
                      'closedcaptions',
                      'desktop',
                      'fullscreen',
                      'fodeviceselection',
                      'hangup',
                      'chat',
                      'raisehand',
                      'settings',
                      'videoquality',
                      'filmstrip',
                      'stats',
                      'tileview',
                      'mute-everyone',
                      'participants-pane'
                    ],
                  }}
                  userInfo={{
                    displayName: user?.name || 'User',
                    email: user?.email || ''
                  }}
                  onReadyToClose={endConference}
                  getIFrameRef={(iframeRef) => {
                    iframeRef.style.height = '100%';
                    iframeRef.style.width = '100%';
                  }}
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
